/**
 * Form field wrapper with label, help text, and error display
 * Integrates with React Hook Form and Zod validation
 *
 * Supports both register-based and Controller-based inputs.
 * Zod errors are automatically mapped from schema validation.
 *
 * @example
 * ```tsx
 * // Register-based (simple inputs)
 * <Field name="email" label="Email" error={errors.email} help="We'll never share your email">
 *   <Input {...register('email')} />
 * </Field>
 *
 * // Controller-based (complex inputs like Select, Checkbox groups)
 * <Field name="role" label="Role" error={errors.role}>
 *   <Controller
 *     name="role"
 *     control={control}
 *     render={({ field }) => (
 *       <Select {...field}>
 *         <option value="admin">Admin</option>
 *         <option value="user">User</option>
 *       </Select>
 *     )}
 *   />
 * </Field>
 * ```
 */

"use client";

import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import type { FieldErrors, Path } from "react-hook-form";
import { Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface FieldProps<T extends Record<string, unknown>> {
  /**
   * Field name (for error lookup)
   */
  name: Path<T>;
  /**
   * Field label
   */
  label?: string;
  /**
   * Error object from React Hook Form
   */
  error?: FieldErrors<T>[Path<T>];
  /**
   * Help text displayed below the field
   */
  help?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * The form control element (Input, Textarea, Select, etc.)
   */
  children: ReactNode;
  /**
   * The margin bottom of the field
   */
  mb?: number;
  /** Playwright selector on the wrapping FormControl */
  "data-testid"?: string;
}

export function Field<T extends Record<string, unknown>>({
  name,
  label,
  error,
  help,
  required = false,
  children,
  mb = 6,
  "data-testid": dataTestId,
}: FieldProps<T>) {
  const isInvalid = !!error;
  const theme = useThemeTokens();

  return (
    <FormControl
      {...(dataTestId ? { "data-testid": dataTestId } : {})}
      isInvalid={isInvalid}
      mb={mb}
      w="100%"
    >
      {label && (
        <FormLabel
          htmlFor={String(name)}
          fontSize="sm"
          fontWeight="500"
          mb={2.5}
          color={theme.text.primary}
          letterSpacing="-0.01em"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      )}
      <Box>{children}</Box>
      {error && (
        <FormErrorMessage mt={1.5} w="100%" display="block">
          <Text
            color={theme.accent.primary}
            fontSize="xs"
            fontWeight="500"
            w="100%"
            display="block"
          >
            {error.message as string}
          </Text>
        </FormErrorMessage>
      )}
      {help && !error && (
        <FormHelperText mt={1.5}>
          <Text variant="tertiary" fontSize="xs">
            {help}
          </Text>
        </FormHelperText>
      )}
    </FormControl>
  );
}
